import type { FC, ReactNode } from 'react'
import type { LayoutProps } from '@/types'

import Link from 'next/link'
import { useRouter } from 'next/router'
import { useEffect, useRef, useState } from 'react'

import {
    ArrowLeftRight,
    Boxes,
    LayoutDashboard,
    Menu,
    PackagePlus,
    Plus,
    Settings
} from 'lucide-react'

import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { useSettings } from '@/components/generals/settings-provider'
import { useInventoryActions } from '@/components/inventory/inventory-actions'
import BrandMark from '@/components/generals/brand-mark'
import { siteConfig } from '@/constants/site'

import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger
} from '@/components/ui/dropdown-menu'

import {
    Sheet,
    SheetContent,
    SheetDescription,
    SheetTitle,
    SheetTrigger
} from '@/components/ui/sheet'

type QuickAction = 'movement' | 'product'

interface SidebarContentProps {
    onNavigate?: () => void
    onQuickAction: (action: QuickAction) => void
}

const NAV_ITEMS = [
    { href: '/', label: 'Panel', icon: LayoutDashboard, match: (path: string) => path === '/' },
    { href: '/inventario', label: 'Inventario', icon: Boxes, match: (path: string) => path.startsWith('/inventario') },
    { href: '/movimientos', label: 'Movimientos', icon: ArrowLeftRight, match: (path: string) => path.startsWith('/movimientos') },
    { href: '/ajustes', label: 'Ajustes', icon: Settings, match: (path: string) => path.startsWith('/ajustes') }
]

const RouteProgress: FC = (): ReactNode => {
    const router = useRouter()
    const [active, setActive] = useState(false)

    useEffect(() => {
        let timer: ReturnType<typeof setTimeout> | null = null

        const start = (_url: string, { shallow }: { shallow: boolean }) => {
            if (shallow) return
            if (timer) clearTimeout(timer)

            timer = setTimeout(() => setActive(true), 150)
        }

        const done = () => {
            if (timer) clearTimeout(timer)

            timer = null
            setActive(false)
        }

        router.events.on('routeChangeStart', start)
        router.events.on('routeChangeComplete', done)
        router.events.on('routeChangeError', done)

        return () => {
            router.events.off('routeChangeStart', start)
            router.events.off('routeChangeComplete', done)
            router.events.off('routeChangeError', done)

            if (timer) clearTimeout(timer)
        }
    }, [router.events])

    return (
        <div
            aria-hidden='true'
            className={cn(
                'pointer-events-none fixed inset-x-0 top-0 z-[60] h-0.5 overflow-hidden transition-opacity duration-200',
                active ? 'opacity-100' : 'opacity-0'
            )}
        >
            <div className='h-full w-1/3 animate-[route-progress_1.1s_ease-in-out_infinite] rounded-full bg-sidebar-primary' />
        </div>
    )
}

const Brand: FC = (): ReactNode => {
    return (
        <Link
            href='/'
            className='flex items-center gap-2.5 rounded-lg outline-none focus-visible:ring-3 focus-visible:ring-sidebar-ring/60'
        >
            <BrandMark />

            <span className='font-heading text-[1.05rem] font-semibold tracking-tight text-sidebar-accent-foreground'>
                {siteConfig.name}
            </span>
        </Link>
    )
}

const SidebarContent: FC<SidebarContentProps> = ({ onNavigate, onQuickAction }): ReactNode => {
    const router = useRouter()
    const { settings } = useSettings()

    return (
        <div className='flex h-full flex-col'>
            <div className='flex h-16 shrink-0 items-center px-5'>
                <Brand />
            </div>

            <div className='px-3'>
                <div className='rounded-lg border border-sidebar-border bg-sidebar-accent/50 px-3 py-2.5'>
                    <p className='text-[0.68rem] font-medium uppercase tracking-[0.08em] text-sidebar-foreground/65'>
                        Negocio
                    </p>

                    <p className='mt-0.5 truncate text-sm font-medium text-sidebar-accent-foreground'>
                        {settings.id ? (
                            <span data-mdlfy-cms={`settings:${settings.id}:business_name`}>{settings.businessName}</span>
                        ) : settings.businessName}
                    </p>
                </div>
            </div>

            <nav aria-label='Secciones' className='mt-5 flex flex-col gap-1 px-3'>
                {NAV_ITEMS.map(item => {
                    const active = item.match(router.pathname)
                    const Icon = item.icon

                    return (
                        <Link
                            key={item.href}
                            href={item.href}
                            onClick={onNavigate}
                            aria-current={active ? 'page' : undefined}
                            className={cn(
                                'relative flex h-10 items-center gap-3 rounded-lg px-3 text-sm font-medium transition-colors outline-none focus-visible:ring-3 focus-visible:ring-sidebar-ring/60',
                                active
                                    ? 'bg-sidebar-accent text-sidebar-accent-foreground'
                                    : 'text-sidebar-foreground hover:bg-sidebar-accent/60 hover:text-sidebar-accent-foreground'
                            )}
                        >
                            {active && <span className='absolute inset-y-2 left-0 w-[3px] rounded-full bg-sidebar-primary' aria-hidden='true' />}

                            <Icon className={cn('size-[1.125rem]', active ? 'text-sidebar-primary' : 'text-sidebar-foreground/70')} aria-hidden='true' />
                            {item.label}
                        </Link>
                    )
                })}
            </nav>

            <div className='mt-auto flex flex-col gap-1.5 border-t border-sidebar-border p-3'>
                <Button
                    size='lg'
                    onClick={() => onQuickAction('movement')}
                    className='justify-start bg-sidebar-primary text-sidebar-primary-foreground hover:bg-sidebar-primary/90'
                >
                    <ArrowLeftRight aria-hidden='true' />
                    Registrar movimiento
                </Button>

                <Button
                    size='lg'
                    variant='ghost'
                    onClick={() => onQuickAction('product')}
                    className='justify-start text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground'
                >
                    <PackagePlus aria-hidden='true' />
                    Nuevo producto
                </Button>
            </div>
        </div>
    )
}

export const Layout: FC<LayoutProps> = ({ children }): ReactNode => {
    const { openMovement, openNewProduct } = useInventoryActions()
    const [menuOpen, setMenuOpen] = useState(false)
    const pendingAction = useRef<QuickAction | null>(null)

    const runAction = (action: QuickAction) => {
        if (action === 'movement') openMovement()
        else openNewProduct()
    }

    const handleMobileQuickAction = (action: QuickAction) => {
        pendingAction.current = action
        setMenuOpen(false)
    }

    return (
        <div className='min-h-screen'>
            <RouteProgress />

            <a
                href='#contenido'
                className='sr-only focus:not-sr-only focus:fixed focus:top-3 focus:left-3 focus:z-[70] focus:rounded-md focus:bg-background focus:px-3 focus:py-2 focus:text-sm focus:font-medium focus:shadow-md'
            >
                Saltar al contenido
            </a>

            <aside className='fixed inset-y-0 left-0 z-30 hidden w-64 border-r border-sidebar-border bg-sidebar text-sidebar-foreground lg:block'>
                <SidebarContent onQuickAction={runAction} />
            </aside>

            <div className='lg:pl-64'>
                <header className='sticky top-0 z-40 flex h-14 items-center gap-2 border-b border-sidebar-border bg-sidebar px-3 text-sidebar-foreground lg:hidden'>
                    <Sheet
                        open={menuOpen}
                        onOpenChange={setMenuOpen}
                        onOpenChangeComplete={open => {
                            if (open || !pendingAction.current) return

                            const action = pendingAction.current

                            pendingAction.current = null
                            runAction(action)
                        }}
                    >
                        <SheetTrigger
                            render={
                                <Button
                                    variant='ghost'
                                    size='icon'
                                    aria-label='Abrir menú'
                                    className='text-sidebar-accent-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground'
                                />
                            }
                        >
                            <Menu aria-hidden='true' />
                        </SheetTrigger>

                        <SheetContent
                            side='left'
                            className='w-72 gap-0 border-sidebar-border bg-sidebar p-0 text-sidebar-foreground [&>[data-slot=sheet-close]]:text-sidebar-foreground'
                        >
                            <SheetTitle className='sr-only'>
                                Menú
                            </SheetTitle>

                            <SheetDescription className='sr-only'>
                                Secciones de {siteConfig.name} y acciones rápidas.
                            </SheetDescription>

                            <SidebarContent
                                onNavigate={() => setMenuOpen(false)}
                                onQuickAction={handleMobileQuickAction}
                            />
                        </SheetContent>
                    </Sheet>

                    <Brand />

                    <div className='ml-auto'>
                        <DropdownMenu>
                            <DropdownMenuTrigger
                                render={
                                    <Button
                                        size='icon'
                                        aria-label='Acciones rápidas'
                                        className='bg-sidebar-primary text-sidebar-primary-foreground hover:bg-sidebar-primary/90'
                                    />
                                }
                            >
                                <Plus aria-hidden='true' />
                            </DropdownMenuTrigger>

                            <DropdownMenuContent align='end' className='w-56'>
                                <DropdownMenuItem onClick={() => openMovement()} className='py-2'>
                                    <ArrowLeftRight aria-hidden='true' />
                                    Registrar movimiento
                                </DropdownMenuItem>

                                <DropdownMenuItem onClick={() => openNewProduct()} className='py-2'>
                                    <PackagePlus aria-hidden='true' />
                                    Nuevo producto
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                    </div>
                </header>

                <main id='contenido' tabIndex={-1} className='mx-auto w-full max-w-7xl px-4 py-6 outline-none sm:px-6 lg:px-8 lg:py-8'>
                    {children}
                </main>
            </div>
        </div>
    )
}
